import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { InteractionManager, Keyboard, Platform, StyleSheet, View, Animated, } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Fuse from 'fuse.js';
import { CommonActions } from '@react-navigation/native';
import Button from 'design-system/Button';
import DSStatusBar from 'design-system/StatusBar';
import getSymbolFromCurrency from 'currency-symbol-map';
import { AppView } from 'design-system/AppView';
import Indicator from 'design-system/Indicator';
import FakeSearchBar from 'design-system/FakeSearchBar';
import { SCROLL_INDICATOR_INSETS, rem } from 'design-system/values';
import ListEmptyView from 'design-system/ListEmptyView';
import NoTransactions from 'features/feed/components/search/NoTransactions';
import NewSearchHeaderFiltered from 'features/feed/components/search/SearchHeaderFiltered';
import NewSearchToolbar from 'features/feed/components/search/SearchToolbar';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import { useDisableAndroidBackButton } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useFetchSearchTransactionData } from 'hooks/useFetchSearchTransactionData';
import { useDeepCompareEffect } from 'hooks/useDeepCompareEffect';
import useAdvancedEditTransactions from 'features/transactions/hooks/useAdvancedEditTransactions';
import { useAppSelector, useAppStore } from 'store/hooks';
import { getTransactionsFlatlistData, } from 'utils/getTransactions';
import { useDefaultHeaderHeight } from 'hooks/useHeaderHeight';
import { useKeyboardHeightWeb } from 'features/feed/components/search/hooks/useKeyboardHeightWeb';
import Reanimated, { useSharedValue, useAnimatedScrollHandler, useAnimatedRef, runOnJS, } from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import useKeyboard from 'features/feed/components/search/hooks/useKeyboard';
import TransactionsHeader from 'features/transactions/components/TransactionsHeader';
import TotalAmountRow from 'features/feed/components/search/TotalAmountRow';
import { focus } from 'hooks/useAutoFocusInput';
import { selectActiveAccountsWithHidden, selectNormalizedCategories, } from 'reducers/selectors';
import useNavigateTransactionItem from 'features/transactions/hooks/useNavigateTransactionItem';
import SearchTransactionRow from './components/SearchTransactionRow';
import { SEARCH_UPGRADE_BANNER_HEIGHT } from './components/SearchTransactionUpgradeBanner';
import { removeEmptyAmountFilters } from './removeEmptyAmountFilters';
import { selectSearchTransactionsFormatted } from './selectors';
import { isAndroid, isDesktopOrWeb, isWeb } from '../../constants';
import { ExportResultModal } from './components/ExportResultModal';
import { getFilteredTransactions, handleSelectCategoryFilter } from './helpers';
const AnimatedFlashList = Reanimated.createAnimatedComponent(FlashList);
// How long to wait for the screen to appear before playing the opening
// animation anyway.
const TRANSITION_FALLBACK_DELAY = 1000;
const searchOptions = {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.2,
    ignoreFieldNorm: true,
    keys: [
        'counterpartName',
        'customName',
        'category.displayName',
        'merchant.displayName',
        'notes',
        'labels',
    ],
};
const fuse = new Fuse([], searchOptions);
const getItemType = (item) => {
    if ('sectionTitle' in item) {
        return 'sectionHeader';
    }
    return 'row';
};
const keyExtractor = (item) => item.id?.toString();
const SearchTransactions = ({ navigation, route, }) => {
    const store = useAppStore();
    const fetchSearchData = useFetchSearchTransactionData();
    const navigateToTransactionItem = useNavigateTransactionItem();
    useEffect(() => {
        if (isWeb) {
            fetchSearchData();
        }
    }, [fetchSearchData]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const { selected, isEditing, selectedObj, selectedLen, onEdit, onSelected, openEditModal, selectAllTransactions, deselectAllTransactions, } = useAdvancedEditTransactions(navigation, true);
    const isEditButtonVisible = isEditing && selected.length !== 0;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: false,
            title: 'Search Transactions',
        });
    }, []);
    const close = useCallback(() => {
        if (navigation.canGoBack()) {
            navigation.goBack();
        }
        else {
            navigation.dispatch(CommonActions.reset({
                index: 0,
                routes: [{ name: 'Feed' }],
            }));
        }
    }, []);
    const { filterType, filterLabel, merchant, category, searchBarY, selectedAccountsObj, } = route.params || {};
    const onSearchCancel = useCallback(async () => {
        if (typeof route.params?.searchBarY === 'number') {
            Animated.timing(progress.current, {
                toValue: 0,
                duration: 200,
                useNativeDriver: true,
            }).start(() => {
                close();
            });
        }
        else {
            close();
        }
    }, [close, route.params?.searchBarY]);
    const backHandler = useCallback(() => {
        onSearchCancel();
        return true;
    }, [onSearchCancel]);
    useDisableAndroidBackButton(backHandler);
    const scrollY = useSharedValue(0);
    const { top } = useSafeAreaInsets();
    const { isUnlockedForSpace, onPressUpgrade } = useShouldShowUpgrade({
        benefitId: 'search',
    });
    const transactions = useAppSelector((store) => selectSearchTransactionsFormatted(store, !isUnlockedForSpace));
    const didFetchSearchTransactions = useAppSelector((store) => store.transactions.didFetchSearchTransactions);
    const tags = useAppSelector((store) => store.transactions.tags);
    const timeoutHandle = useRef(undefined);
    useEffect(() => () => {
        clearTimeout(timeoutHandle.current);
    }, []);
    // Opened from a search bar, the screen is pushed without a native animation
    // and this one plays instead. On the New Architecture the screen appears
    // 100-350 ms after mounting, so started on mount the animation had finished
    // before the screen showed. Start it once the screen has appeared.
    const opensFromSearchBar = typeof searchBarY === 'number';
    useEffect(() => {
        if (isWeb) {
            return undefined;
        }
        const animateIn = () => {
            Animated.timing(progress.current, {
                toValue: 1,
                duration: 350,
                useNativeDriver: true,
            }).start();
        };
        if (!opensFromSearchBar) {
            animateIn();
            return undefined;
        }
        const unsubscribe = navigation.addListener('transitionEnd', (e) => {
            if (!e.data.closing) {
                cleanUp();
                animateIn();
            }
        });
        const fallback = setTimeout(() => {
            cleanUp();
            animateIn();
        }, TRANSITION_FALLBACK_DELAY);
        const cleanUp = () => {
            unsubscribe();
            clearTimeout(fallback);
        };
        return cleanUp;
    }, [navigation, opensFromSearchBar]);
    const refSearchComponent = useRef(null);
    const refSearchBar = useRef(null);
    const refAmountLowerInput = useRef(null);
    const refAmountUpperInput = useRef(null);
    const scrollViewRef = useAnimatedRef();
    const getActiveFilter = useCallback((oldActiveFilters) => {
        const activeFilter = { ...oldActiveFilters };
        // set default filter by tag
        if (filterType === 'TAG' && !!filterLabel) {
            activeFilter[filterType] = [
                {
                    label: filterLabel,
                    badgeText: `#${filterLabel}`,
                    filterType,
                },
            ];
        }
        if (filterType === 'MERCHANT' && merchant) {
            const merchantObj = {
                ...merchant,
                id: merchant.id,
                badgeText: merchant.displayName,
                filterType,
            };
            activeFilter[filterType] = [merchantObj];
        }
        if (filterType === 'CATEGORY' && category) {
            activeFilter[filterType] = [
                {
                    ...category,
                    badgeText: category.displayName,
                    filterType,
                    isUserSelected: true,
                },
            ];
        }
        if (selectedAccountsObj && Object.keys(selectedAccountsObj).length) {
            const activeAccounts = selectActiveAccountsWithHidden(store.getState());
            const accounts = activeAccounts.reduce((prev, curr) => {
                if (selectedAccountsObj[curr.id]) {
                    prev.push({
                        ...curr,
                        badgeText: curr.name,
                        filterType: 'ACCOUNT',
                    });
                }
                return prev;
            }, []);
            if (accounts?.length) {
                activeFilter.ACCOUNT = accounts;
            }
        }
        return activeFilter;
    }, [category, filterLabel, filterType, merchant, selectedAccountsObj, tags]);
    const currency = useAppSelector((store) => store.user.user.currency || 'GBP');
    const currencySymbol = useMemo(() => getSymbolFromCurrency(currency) || currency, [currency]);
    const normalizedCategories = useAppSelector(selectNormalizedCategories);
    const [state, setState] = useState(() => {
        const defaultAmount = {
            value: '15',
            badgeText: currencySymbol,
            filterType: 'AMOUNT_UPPER',
        };
        const defaultCategory = {
            ...normalizedCategories.bills,
            id: normalizedCategories.bills?.id || 'bills',
            badgeText: normalizedCategories.bills?.displayName || 'Bills',
            filterType: 'CATEGORY',
            isUserSelected: true,
        };
        const activeFilters = getActiveFilter();
        return {
            defaultAmount,
            flashListTransactions: [],
            searchValue: '',
            listPadding: 0,
            activeFilter: activeFilters,
            loadingResultsFirstLoad: true,
            totalAmount: null,
            numberOfTransactions: 0,
            defaultCategory,
            settingsFilter: 'DEFAULT',
            isKeyboardVisible: true,
            withInitialFilters: !!Object.keys(activeFilters).length,
        };
    });
    const filterResponse = useCallback((dataInput) => {
        clearTimeout(timeoutHandle.current);
        timeoutHandle.current = setTimeout(() => {
            const activeStateFilter = { ...state.activeFilter };
            // All the search terms comes from dataInput
            delete activeStateFilter.TEXTINPUT;
            const wordsToMatch = [];
            if (dataInput?.length ||
                (activeStateFilter && Object.keys(activeStateFilter).length)) {
                const filteredTransactions = getFilteredTransactions(transactions, state.settingsFilter, fuse, dataInput, activeStateFilter);
                const flashListTransactions = getTransactionsFlatlistData(filteredTransactions, false);
                let totalAmount = 0;
                if (filteredTransactions && filteredTransactions.length > 0) {
                    const isActiveExcludedFilter = activeStateFilter &&
                        Object.keys(activeStateFilter).length &&
                        activeStateFilter.CATEGORY &&
                        activeStateFilter.CATEGORY.find((category) => 'id' in category && category.id === 'internal');
                    const isExcludedCategoryInSearch = isActiveExcludedFilter ||
                        (dataInput &&
                            wordsToMatch.every((word) => word.match('excluded')));
                    totalAmount = filteredTransactions.reduce((prev, next) => {
                        //  count excluded if selected in search
                        if (isExcludedCategoryInSearch &&
                            next.category &&
                            next.category.id === 'internal') {
                            return prev + next.amount;
                        }
                        if (next.category?.id === 'internal') {
                            return prev;
                        }
                        return prev + next.amount;
                    }, 0);
                }
                setState((oldState) => ({
                    ...oldState,
                    flashListTransactions,
                    loadingResultsFirstLoad: false,
                    totalAmount,
                    numberOfTransactions: filteredTransactions.length,
                }));
            }
            else {
                setState((oldState) => ({
                    ...oldState,
                    numberOfTransactions: 0,
                    totalAmount: null,
                    flashListTransactions: [],
                    loadingResultsFirstLoad: true,
                }));
            }
        }, 0);
    }, 
    // we dont want to include state.activeFilter.TEXTINPUT
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        state.settingsFilter,
        state.activeFilter.ACCOUNT,
        state.activeFilter.AMOUNT_LOWER,
        state.activeFilter.AMOUNT_UPPER,
        state.activeFilter.CATEGORY,
        state.activeFilter.DATES,
        state.activeFilter.MERCHANT,
        state.activeFilter.RECEIPT,
        state.activeFilter.TAG,
        transactions,
    ]);
    const onChangeText = useCallback((dataInput) => setState((state) => ({
        ...state,
        searchValue: dataInput || '',
    })), []);
    useEffect(() => {
        const keyboardDidShow = (e) => {
            setState((oldState) => ({
                ...oldState,
                listPadding: e.endCoordinates.height,
                isKeyboardVisible: true,
            }));
        };
        const keyboardDidHide = () => {
            setState((oldState) => ({
                ...oldState,
                listPadding: 0,
                isKeyboardVisible: false,
            }));
        };
        const keyboardDidShowListener = Keyboard.addListener('keyboardDidShow', keyboardDidShow);
        const keyboardDidHideListener = Keyboard.addListener('keyboardDidHide', keyboardDidHide);
        return () => {
            keyboardDidShowListener?.remove();
            keyboardDidHideListener?.remove();
        };
    }, [close, filterResponse]);
    const onRemoveFilterCleanup = useCallback(() => {
        if (isAndroid) {
            InteractionManager.runAfterInteractions(() => {
                scrollViewRef.current?.scrollToEnd({ animated: true });
            });
        }
    }, [scrollViewRef]);
    const onSelectFilter = useCallback((newFilter) => {
        if (newFilter) {
            let didRemove = false;
            setState((state) => {
                if (!state.activeFilter)
                    return state;
                const newActiveFilter = { ...state.activeFilter };
                const newFilterType = newFilter.filterType;
                if (newFilterType === 'TAG' ||
                    newFilterType === 'CATEGORY' ||
                    newFilterType === 'MERCHANT' ||
                    newFilterType === 'ACCOUNT' ||
                    newFilterType === 'TEXTINPUT') {
                    const oldFilters = newActiveFilter[newFilterType];
                    if (oldFilters?.length) {
                        // @ts-ignore
                        newActiveFilter[newFilterType] = [...oldFilters];
                    }
                    else {
                        newActiveFilter[newFilterType] = [];
                    }
                    if (state.settingsFilter !== 'AND' &&
                        newFilter.filterType === 'CATEGORY' &&
                        (newFilter.subCategoryIds?.length || newFilter.parentCategoryId)) {
                        const categoryFilter = handleSelectCategoryFilter(newActiveFilter[newFilter.filterType] ?? [], newFilter, store.getState());
                        if (!categoryFilter.length) {
                            delete newActiveFilter[newFilterType];
                        }
                        else {
                            newActiveFilter[newFilter.filterType] = categoryFilter;
                        }
                        return {
                            ...state,
                            activeFilter: newActiveFilter,
                            searchValue: '',
                        };
                    }
                    // @ts-ignore
                    newActiveFilter[newFilterType] = newActiveFilter[newFilterType]?.filter((e) => {
                        const condition = ('id' in e &&
                            'id' in newFilter &&
                            newFilter.id &&
                            newFilter.id === e.id) ||
                            ('displayName' in newFilter &&
                                newFilter.displayName &&
                                'displayName' in e &&
                                newFilter.displayName === e.displayName) ||
                            ('label' in newFilter &&
                                newFilter.label &&
                                'label' in e &&
                                newFilter.label === e.label);
                        if (condition) {
                            didRemove = true;
                        }
                        return !condition;
                    });
                    if (!didRemove) {
                        if (state.settingsFilter === 'AND' &&
                            newFilterType !== 'TEXTINPUT') {
                            // @ts-ignore
                            newActiveFilter[newFilterType] = [newFilter];
                        }
                        else {
                            // @ts-ignore
                            newActiveFilter[newFilterType]?.push(newFilter);
                        }
                    }
                    if (!newActiveFilter[newFilterType]?.length) {
                        delete newActiveFilter[newFilterType];
                    }
                }
                else {
                    // @ts-ignore
                    newActiveFilter[newFilterType] = newFilter;
                }
                return {
                    ...state,
                    activeFilter: newActiveFilter,
                    searchValue: newFilterType === 'TEXTINPUT' ? '' : state.searchValue,
                };
            });
            if (didRemove) {
                onRemoveFilterCleanup();
            }
        }
    }, [onRemoveFilterCleanup]);
    const onSelectAll = useCallback((newFilter) => {
        if (newFilter.length) {
            const key = 'bankConnectionId' in newFilter[0] ? 'ACCOUNT' : 'CATEGORY';
            setState((oldState) => ({
                ...oldState,
                activeFilter: {
                    ...oldState.activeFilter,
                    [key]: newFilter,
                },
            }));
        }
    }, []);
    const onRemoveFilter = useCallback((filterToRemove) => {
        onEdit(false);
        if (!filterToRemove || !state.activeFilter) {
            onRemoveFilterCleanup();
            return;
        }
        if (filterToRemove === 'CLEAR_FILTERS') {
            setState((oldState) => ({
                ...oldState,
                activeFilter: {},
                searchValue: '',
                loadingResultsFirstLoad: true,
                numberOfTransactions: 0,
                totalAmount: null,
            }));
            scrollY.value = 0;
        }
        else if (typeof filterToRemove === 'string') {
            setState((oldState) => {
                const newActiveFilter = { ...oldState.activeFilter };
                delete newActiveFilter[filterToRemove];
                if (Object.keys(newActiveFilter)?.length || oldState.searchValue) {
                    return {
                        ...oldState,
                        activeFilter: newActiveFilter,
                    };
                }
                return {
                    ...oldState,
                    activeFilter: newActiveFilter,
                    loadingResultsFirstLoad: true,
                    numberOfTransactions: 0,
                    totalAmount: null,
                };
            });
            scrollY.value = 0;
        }
        else {
            const filterTypeToRemove = filterToRemove.filterType;
            if (filterTypeToRemove === 'TAG' ||
                filterTypeToRemove === 'CATEGORY' ||
                filterTypeToRemove === 'MERCHANT' ||
                filterTypeToRemove === 'ACCOUNT' ||
                filterTypeToRemove === 'TEXTINPUT') {
                setState((oldState) => {
                    const activeFilter = {
                        ...oldState.activeFilter,
                    };
                    const oldFilters = activeFilter[filterTypeToRemove];
                    if (oldFilters?.length) {
                        // @ts-ignore
                        activeFilter[filterTypeToRemove] = oldFilters.filter(
                        // @ts-ignore
                        (e) => e !== filterToRemove);
                    }
                    // @ts-ignore  removing the key if there are no more filters in the array
                    if (activeFilter[filterTypeToRemove]?.length < 1) {
                        delete activeFilter[filterTypeToRemove];
                    }
                    if (!Object.keys(activeFilter).length || oldState.searchValue) {
                        return {
                            ...oldState,
                            activeFilter,
                            loadingResultsFirstLoad: true,
                            numberOfTransactions: 0,
                            totalAmount: null,
                        };
                    }
                    return { ...oldState, activeFilter };
                });
            }
            else {
                setState((oldState) => {
                    const newActiveFilter = {
                        ...oldState.activeFilter,
                    };
                    delete newActiveFilter[filterTypeToRemove];
                    if (Object.keys(newActiveFilter)?.length || oldState.searchValue) {
                        return {
                            ...oldState,
                            activeFilter: newActiveFilter,
                        };
                    }
                    return {
                        ...oldState,
                        activeFilter: newActiveFilter,
                        loadingResultsFirstLoad: true,
                        numberOfTransactions: 0,
                        totalAmount: null,
                    };
                });
            }
            scrollY.value = 0;
            InteractionManager.runAfterInteractions(() => {
                if (filterToRemove.filterType === 'AMOUNT_UPPER' ||
                    filterToRemove.filterType === 'AMOUNT_LOWER') {
                    refSearchBar.current?.focus();
                }
            });
        }
        onRemoveFilterCleanup();
    }, [onEdit, onRemoveFilterCleanup, scrollY, state.activeFilter]);
    useEffect(() => {
        if (!state.numberOfTransactions) {
            scrollY.value = 0;
            onEdit(false);
        }
    }, [onEdit, scrollY, state.numberOfTransactions]);
    const clearEmptyAmountFilter = useCallback((filterType) => {
        setState((prevState) => ({
            ...prevState,
            activeFilter: removeEmptyAmountFilters(prevState.activeFilter, filterType),
        }));
    }, []);
    const clearEmptyTextInputFilters = useCallback(() => {
        setState((prevState) => {
            if (prevState.activeFilter) {
                const textInputFilters = prevState.activeFilter.TEXTINPUT;
                if (textInputFilters?.length) {
                    const filteredEmptyArr = textInputFilters.filter((filter) => filter.label);
                    const newActiveFilter = { ...prevState.activeFilter };
                    if (filteredEmptyArr.length) {
                        newActiveFilter.TEXTINPUT = filteredEmptyArr;
                    }
                    else {
                        delete newActiveFilter.TEXTINPUT;
                    }
                    return {
                        ...prevState,
                        activeFilter: newActiveFilter,
                    };
                }
            }
            return prevState;
        });
    }, []);
    const cancelEdit = useCallback(() => {
        onEdit(false);
    }, [onEdit]);
    const addNewAmountFilter = useCallback((filterType) => {
        if (!filterType) {
            return;
        }
        if (state.activeFilter[filterType]) {
            clearEmptyAmountFilter(filterType);
            return;
        }
        const newFilter = {
            ...state.activeFilter,
            [filterType]: {
                value: '',
                badgeText: currencySymbol,
                filterType,
            },
        };
        setState((oldState) => ({
            ...oldState,
            activeFilter: removeEmptyAmountFilters(newFilter, filterType),
        }));
    }, [clearEmptyAmountFilter, currencySymbol, state.activeFilter]);
    const onToggleAmountFilter = useCallback((filterType) => {
        if (filterType) {
            // New amount filter has been selected
            addNewAmountFilter(filterType);
        }
        else {
            // No amount filter has been selected
            clearEmptyAmountFilter(undefined);
        }
    }, [addNewAmountFilter, clearEmptyAmountFilter]);
    const onChangeSettingsFilter = useCallback((newFilter) => {
        setState((oldState) => {
            if (newFilter !== oldState.settingsFilter) {
                return {
                    ...oldState,
                    settingsFilter: newFilter,
                    activeFilter: {},
                    searchValue: '',
                    loadingResultsFirstLoad: true,
                    totalAmount: null,
                    numberOfTransactions: 0,
                };
            }
            return oldState;
        });
    }, []);
    const { keyboardHeight, activeKeyboard, showSpecialKeyboard, keyboardHideFromScroll, } = useKeyboard(state.activeFilter, onSelectFilter, onRemoveFilter, onToggleAmountFilter);
    // creating an array of search texts
    const searchTextArr = useMemo(() => {
        const searchFilters = state.activeFilter?.TEXTINPUT;
        const arr = state.searchValue ? [state.searchValue] : [];
        searchFilters?.forEach((searchFilter) => {
            arr.push(searchFilter.label);
        });
        arr.sort();
        return arr;
    }, [state.activeFilter?.TEXTINPUT, state.searchValue]);
    /**
     * This hook listens to change in filterResponse<state.activeFilters, transactions>
     * and searchTextArr to update the list
     * we only run the effect if the shallow copy of the searchTextArr is changed or debouncedFilter is changed
     */
    useDeepCompareEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            if (didFetchSearchTransactions) {
                filterResponse(searchTextArr);
            }
        });
    }, [searchTextArr, filterResponse, didFetchSearchTransactions], [0]);
    const applyFilterDefaultCateg = useCallback(() => {
        onSelectFilter(state.defaultCategory);
    }, [onSelectFilter, state.defaultCategory]);
    const applyFilterDefaultAmount = useCallback(() => {
        onToggleAmountFilter('AMOUNT_UPPER');
        onSelectFilter(state.defaultAmount);
        refSearchComponent.current?.onSetDefaultAmount(state.defaultAmount.value);
    }, [onSelectFilter, onToggleAmountFilter, state.defaultAmount]);
    const renderNoTransactions = useCallback(() => (<NoTransactions style={paddingHorizontalStyle} defaultAmount={state.defaultAmount.value} defaultCategoryName={state.defaultCategory.badgeText} onApplyDefaultCategoryFilter={applyFilterDefaultCateg} onApplyDefaultAmountFilter={applyFilterDefaultAmount}/>), [
        applyFilterDefaultAmount,
        applyFilterDefaultCateg,
        paddingHorizontalStyle,
        state.defaultAmount.value,
        state.defaultCategory.badgeText,
    ]);
    const renderNoResults = useCallback(() => (<View style={[styles.noTransactionsContainer, paddingHorizontalStyle]}>
        <ListEmptyView alt happy onCard title="No results found" subTitle="Try a different search term or filter"/>
      </View>), [paddingHorizontalStyle]);
    const openItem = useCallback((data) => {
        if (!isUnlockedForSpace) {
            onPressUpgrade();
            return;
        }
        if (isEditing) {
            if (data.isPending)
                return;
            onSelected(data);
            return;
        }
        navigateToTransactionItem(data, true);
    }, [
        isEditing,
        isUnlockedForSpace,
        navigateToTransactionItem,
        onPressUpgrade,
        onSelected,
    ]);
    const renderRow = useCallback(({ item }) => (<SearchTransactionRow item={item} openItem={openItem} isEditingTransactions={isEditing} selected={!!selectedObj[item.id]}/>), [openItem, isEditing, selectedObj]);
    const onScrollBeginDrag = useCallback(() => {
        keyboardHideFromScroll();
        Keyboard.dismiss();
    }, [keyboardHideFromScroll]);
    const { bottom } = useSafeAreaInsets();
    const webKeyboardHeight = useKeyboardHeightWeb();
    const contentContainerStyle = useMemo(() => ({
        ...paddingHorizontalStyle,
        paddingBottom: bottom +
            state.listPadding +
            (Platform.OS === 'android' ? rem(200) : rem(116)) +
            (isUnlockedForSpace ? 0 : SEARCH_UPGRADE_BANNER_HEIGHT) +
            (isDesktopOrWeb ? webKeyboardHeight : 0),
    }), [
        bottom,
        isUnlockedForSpace,
        paddingHorizontalStyle,
        state.listPadding,
        webKeyboardHeight,
    ]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
        if (isWeb) {
            runOnJS(showSpecialKeyboard)(undefined);
        }
    }, []);
    const noFilter = useMemo(() => !state.searchValue &&
        (!state.activeFilter || !Object.keys(state.activeFilter).length) &&
        !selectedLen, [selectedLen, state.activeFilter, state.searchValue]);
    const Content = useMemo(() => {
        if (noFilter) {
            return renderNoTransactions();
        }
        if (!state.flashListTransactions.length) {
            if (state.loadingResultsFirstLoad) {
                return (<Indicator dark style={[styles.noTransactionsContainer, styles.indicatorContainer]}/>);
            }
            scrollY.value = 0;
            return renderNoResults();
        }
        const baseProps = {
            keyExtractor,
            onScrollBeginDrag,
            renderItem: renderRow,
            contentContainerStyle,
            onScroll: scrollHandler,
            scrollEventThrottle: 16,
            data: state.flashListTransactions,
            scrollIndicatorInsets: SCROLL_INDICATOR_INSETS,
            showsVerticalScrollIndicator: true,
            keyboardShouldPersistTaps: 'always',
        };
        if (isWeb) {
            return <Reanimated.FlatList {...baseProps}/>;
        }
        return (<AnimatedFlashList {...baseProps} getItemType={getItemType} onScrollBeginDrag={onScrollBeginDrag} extraData={[isEditing, renderRow, contentContainerStyle]}/>);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.loadingResultsFirstLoad,
        state.flashListTransactions,
        noFilter,
        onScrollBeginDrag,
        renderRow,
        contentContainerStyle,
        scrollHandler,
        isEditing,
        selectedObj,
        renderNoTransactions,
        scrollY,
        renderNoResults,
    ]);
    const onSearchInputFocus = useCallback(() => {
        onEdit(false);
        showSpecialKeyboard(undefined);
    }, [onEdit, showSpecialKeyboard]);
    const onSearchInputBlur = useCallback(() => {
        const inputValue = state.searchValue;
        if (inputValue) {
            onSelectFilter({
                label: inputValue,
                filterType: 'TEXTINPUT',
            });
        }
    }, [onSelectFilter, state.searchValue]);
    const progress = useRef(new Animated.Value(isWeb ? 1 : 0));
    const mainStyles = useMemo(() => [
        {
            opacity: progress.current?.interpolate({
                inputRange: [0.5, 1],
                outputRange: [0, 1],
                extrapolate: 'clamp',
            }),
        },
    ], []);
    const headerHeight = useDefaultHeaderHeight();
    const fakeSearchBarStyles = useMemo(() => [
        styles.fakeSearchBar,
        {
            top,
            transform: [
                {
                    translateY: progress.current?.interpolate({
                        inputRange: [0, 0.5],
                        outputRange: [searchBarY || 0, headerHeight - top],
                        extrapolate: 'clamp',
                    }),
                },
            ],
            opacity: progress.current?.interpolate({
                inputRange: [0.5, 1],
                outputRange: [1, 0],
                extrapolate: 'clamp',
            }),
        },
    ], [headerHeight, searchBarY, top]);
    const onSearchValueChange = useCallback((newText, index) => {
        setState((oldState) => {
            const textInputFilters = oldState.activeFilter.TEXTINPUT;
            if (textInputFilters?.length && textInputFilters[index]) {
                return {
                    ...oldState,
                    activeFilter: {
                        ...oldState.activeFilter,
                        TEXTINPUT: textInputFilters.map((textInputFilter, filterIndex) => {
                            if (filterIndex === index) {
                                return {
                                    ...textInputFilter,
                                    label: newText,
                                };
                            }
                            return textInputFilter;
                        }),
                    },
                };
            }
            return oldState;
        });
    }, []);
    const onEditTransactions = useCallback(() => {
        keyboardHideFromScroll();
        Keyboard.dismiss();
        openEditModal();
    }, [keyboardHideFromScroll, openEditModal]);
    const EditTransactionBtn = useMemo(() => {
        if (isEditButtonVisible) {
            return (<Button brand floating title={selectedLen === 1
                    ? 'Edit transaction'
                    : `Edit (${selectedLen}) transactions`} onPress={onEditTransactions}/>);
        }
        return null;
    }, [isEditButtonVisible, onEditTransactions, selectedLen]);
    const handleEditPress = useCallback((edit) => {
        Keyboard.dismiss();
        keyboardHideFromScroll();
        if (isWeb) {
            showSpecialKeyboard(undefined);
        }
        onEdit(edit);
    }, [keyboardHideFromScroll, onEdit, showSpecialKeyboard]);
    const onExport = useCallback(() => {
        Keyboard.dismiss();
        const accountIds = new Set();
        const transactionIds = state.flashListTransactions
            .filter((txn) => 'accountId' in txn)
            .map((txn) => {
            accountIds.add(txn.accountId);
            return txn.id;
        });
        ExportResultModal.show({
            accountIds: Array.from(accountIds),
            transactionIds,
            onAdjustFilters: () => {
                ExportResultModal.hide();
                focus(refSearchBar);
            },
        });
    }, [state.flashListTransactions]);
    const RightContent = useMemo(() => {
        if (!noFilter && !!state.numberOfTransactions) {
            return (<View style={styles.headerRight}>
          <TransactionsHeader showExport onPressEdit={handleEditPress} isEditing={isEditing} onExport={onExport}/>
        </View>);
        }
        return null;
    }, [
        handleEditPress,
        isEditing,
        noFilter,
        onExport,
        state.numberOfTransactions,
    ]);
    const onSelectUnselectAll = useCallback(() => {
        if (selectedLen === state.numberOfTransactions) {
            deselectAllTransactions();
        }
        else {
            const flatListTransactions = state.flashListTransactions.filter((txn) => 'accountId' in txn);
            selectAllTransactions(flatListTransactions);
        }
    }, [
        deselectAllTransactions,
        selectAllTransactions,
        selectedLen,
        state.flashListTransactions,
        state.numberOfTransactions,
    ]);
    return (<View style={styles.container}>
      <AppView type="secondary" withFrame={false}>
        <DSStatusBar barStyle="dark-content" animated/>
        <Animated.View style={[mainStyles, styles.container]}>
          <View style={styles.container}>
            <NewSearchHeaderFiltered cancelEdit={cancelEdit} ref={refSearchComponent} refSearchBar={refSearchBar} refAmountLowerInput={refAmountLowerInput} refAmountUpperInput={refAmountUpperInput} onCancel={onSearchCancel} searchValue={state.searchValue} onChangeText={onChangeText} onInputFocus={onSearchInputFocus} onInputBlur={onSearchInputBlur} activeFilter={state.activeFilter} onSelectFilter={onSelectFilter} onRemoveFilter={onRemoveFilter} scrollY={scrollY} onToggleAmountFilter={onToggleAmountFilter} activeKeyboard={activeKeyboard} onSearchValueChange={onSearchValueChange} currencySymbol={currencySymbol} showSpecialKeyboard={showSpecialKeyboard} clearEmptyTextInputFilters={clearEmptyTextInputFilters} scrollViewRef={scrollViewRef} headerRight={RightContent}>
              {!noFilter && (<TotalAmountRow currency={currency} isEditing={isEditing} totalAmount={state.totalAmount || 0} onSelectUnselectAll={onSelectUnselectAll} numberOfTransactions={state.numberOfTransactions} isAllSelected={selectedLen === state.numberOfTransactions}/>)}
            </NewSearchHeaderFiltered>
            {Content}
            {isWeb && isEditing ? null : (<NewSearchToolbar settingsFilter={state.settingsFilter} refAmountLowerInput={refAmountLowerInput} refAmountUpperInput={refAmountUpperInput} activeFilter={state.activeFilter} onSelectFilter={onSelectFilter} isUnlocked={isUnlockedForSpace} onPressUpgrade={onPressUpgrade} keyboardHeight={keyboardHeight} activeKeyboard={activeKeyboard} onRemoveFilter={onRemoveFilter} onSelectAll={onSelectAll} showSpecialKeyboard={showSpecialKeyboard} onChangeSettingsFilter={onChangeSettingsFilter}/>)}
          </View>
        </Animated.View>
        {EditTransactionBtn}
      </AppView>
      {!!searchBarY && (<Animated.View style={[fakeSearchBarStyles, paddingHorizontalStyle]} pointerEvents="none">
          <FakeSearchBar />
        </Animated.View>)}
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    noTransactionsContainer: {
        marginTop: rem(16),
        flexDirection: 'row',
    },
    indicatorContainer: {
        bottom: rem(140),
    },
    fakeSearchBar: {
        left: 0,
        right: 0,
        position: 'absolute',
    },
    headerRight: {
        flex: 1,
        alignItems: 'flex-end',
        justifyContent: 'center',
        paddingRight: isWeb ? 0 : rem(16),
    },
});
export default SearchTransactions;
